import type {
  ContractRecord,
  CounterRecordMap
} from '@libs/data-access/entities'
import { isContractInProgressAt } from '@libs/data-access/queries'
import type { RestIterable } from '@spa/constants/RestTypes'
import RestTypes, {
  DEFAULT_REST,
  REST_ACCIDENT_ID,
  REST_ACCIDENT_LEGACY_ID
} from '@spa/constants/RestTypes'
import { formatToIsoDay } from '@spa/utils/date'
import { hasCompensatoryCounterAccess } from 'snap-redux/modules/contracts/helpers'

const EXTRA = 'extra'
const INTERIM = 'interim'

const today = new Date()

/**
 * Filtering rest types depending on contract selected and rest type
 */
const RestTypesWithoutDefaultRest = RestTypes.filterNot(
  // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
  type => type.id === DEFAULT_REST.id || !type.usable_in_rest_form
)

const getWithoutRTT = (restTypes: RestIterable) =>
  // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
  restTypes.filterNot(restType => restType.id === 'rtt')

const getWithoutRestCounters = (restTypes: RestIterable) =>
  // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
  restTypes.filterNot(restType => Boolean(restType.counter_type))

const getWithoutDailyWorker = (restTypes: RestIterable) =>
  // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
  restTypes.filterNot(restType => restType.only_for_daily_workers)

type RestTypesArgs = {
  contract: ContractRecord
  restTypes: RestIterable
}

const getWithPublicHolidayCompensatory = ({
  contract,
  restTypes
}: RestTypesArgs) =>
  restTypes.filter(restType => {
    if (
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      restType.id === 'public_holiday_compensatory' &&
      contract?.contract_type !== null
    ) {
      return true
    }
    return true
  })

/**
 * getWithCompensatoryCounters
 * Filter RestTypes with :
 * - compensatory_rest if has access
 * - public_holiday_compensatory if has access
 */
const getWithCompensatoryRest = ({
  contract,
  restTypes,
  fullTimeThreshold,
  shouldShowRCRForPartTime
}) =>
  restTypes.filter(restType => {
    if (restType.id === 'compensatory_rest') {
      const contractIsInProgress = isContractInProgressAt(
        contract,
        formatToIsoDay(today)
      )

      return (
        hasCompensatoryCounterAccess(
          contract,
          fullTimeThreshold,
          shouldShowRCRForPartTime
        ) && contractIsInProgress
      )
    }

    return true
  })

/**
 * useSelectableRestTypes
 * Set the initial rests list when opening rest form with initial contractId
 */
type SelectableRestTypesArgs = {
  contract: ContractRecord
  restId: number
  restUsesCounter: boolean
  counters: CounterRecordMap
  shouldShowPublicHolidayCompensatory: boolean
  isPublicHolidayV2: boolean
  shouldShowRCRForPartTime: boolean
  fullTimeThreshold: number
  shouldHideAccidentByHours: boolean
  isEligibleToRtt?: boolean
}

export const getSelectableRestTypes = ({
  contract,
  restId,
  restUsesCounter,
  shouldShowRCRForPartTime,
  isEligibleToRtt,
  fullTimeThreshold,
  shouldHideAccidentByHours
}: SelectableRestTypesArgs): RestIterable => {
  const isDailyWorker = contract?.daily_worker
  const isExtra = contract.contract_type === EXTRA
  const isInterim = contract.contract_type === INTERIM
  const isPartTime = contract.contract_time && contract.contract_time < 35

  let restTypes = isEligibleToRtt
    ? RestTypesWithoutDefaultRest
    : getWithoutRTT(RestTypesWithoutDefaultRest)

  restTypes = restTypes.filterNot(
    restType =>
      restType?.id ===
      (shouldHideAccidentByHours ? REST_ACCIDENT_LEGACY_ID : REST_ACCIDENT_ID)
  )

  if (isExtra || isInterim || isPartTime) {
    const withCompensatoryRest = getWithCompensatoryRest({
      contract,
      restTypes,
      fullTimeThreshold,
      shouldShowRCRForPartTime
    })
    const withPublicHolidayCompensatory = getWithPublicHolidayCompensatory({
      contract,
      restTypes: withCompensatoryRest
    })
    return getWithoutDailyWorker(withPublicHolidayCompensatory)
  }

  if (!isDailyWorker && restId && !restUsesCounter) {
    const restTypesWithoutRestCounter = getWithoutRestCounters(restTypes)
    return getWithoutDailyWorker(restTypesWithoutRestCounter)
  }

  if (isDailyWorker || (restId && !restUsesCounter)) {
    return getWithoutRestCounters(restTypes)
  }

  if (!isDailyWorker) {
    const withCompensatoryRest = getWithCompensatoryRest({
      contract,
      restTypes,
      fullTimeThreshold,
      shouldShowRCRForPartTime
    })
    const withPublicHolidayCompensatory = getWithPublicHolidayCompensatory({
      contract,
      restTypes: withCompensatoryRest
    })
    return getWithoutDailyWorker(withPublicHolidayCompensatory)
  }

  return restTypes
}
